import {
  Box,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  InputAdornment,
  TextField,
  Button as MuiButton,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeftLong, FaEye, FaEyeSlash, FaPlus } from "react-icons/fa6";
import { FaEdit, } from "react-icons/fa";
import { GrClose } from "react-icons/gr";
import { ToastContainer, toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";
import { IoSearch } from "react-icons/io5";
import { useCompany } from "../hooks/hooks";
import ExcelDownloadButton from "../utils/ExcelDownloader";

const Inputs = [
  { name: "user_name", placeholder: "User Name" },
  { name: "password", placeholder: "Password" },
  { name: "user_level", placeholder: "User Level" },
  { name: "mobile_no", placeholder: "Mobile No" },
  { name: "email", placeholder: "Email" },
];

const EditInputs = [
  { name: "user_name", placeholder: "User Name" },
  { name: "password", placeholder: "Password" },
  { name: "user_level", placeholder: "User Level" },
  { name: "mobile_no", placeholder: "Mobile No" },
  { name: "email", placeholder: "Email" },
  { name: "is_active", placeholder: "Active" },
  { name: "remark", placeholder: "Remark" },
];

export const EtSignUsers = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const { id } = useParams();

  const [users, setUsers] = useState([]);
  const [add_user, setAddUser] = useState({});
  const [edit_user, setEditUser] = useState({});
  const [selected_user, setSelectedUser] = useState({});
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [visible_password, setVisiblePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);
  const handleOpenEdit = () => setOpenEdit(true);
  const handleCloseEdit = () => setOpenEdit(false);

  useEffect(() => {
    getUsers(id);
  }, [id]);

  useEffect(() => {
    setFilteredUsers(
      users.filter((u) =>
        u.user_name.toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [searchText, users]);

  const getUsers = async (companyId) => {
    try {
      const res = await axios.get(
        `https://application.eterp.in/users/company/master/${companyId}`
      );
      setUsers(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  const addUser = async (payload) => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        `https://application.eterp.in/users/master`,
        { ...payload, company_id: id }
      );
      toast.success(res.data.message);
      getUsers(id);
      handleCloseAdd();
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const editUser = async (userId, payload) => {
    setIsLoading(true);

    try {
      const res = await axios.patch(
        `https://application.eterp.in/users/master/${userId}`,
        payload
      );
      toast.success(res.data.message);
      getUsers(id);
      handleCloseEdit();
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddUser({ ...add_user, [name]: value });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    console.log({ [name]: value });

    setEditUser({ ...edit_user, [name]: value });
  };

  return (
    <Box sx={{ padding: { xs: 2, md: 4 } }}>
      <ToastContainer />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          marginBottom: 3,
        }}
      >
        {/* Left side: Back arrow + company name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FaArrowLeftLong
            size={30}
            color="#2db7e8"
            style={{ cursor: "pointer" }}
            onClick={() => navigate(-1)}
          />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {company.name}
          </Typography>
        </Box>

        {/* Right side: Search + Add User + Export */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search users..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: "#2db7e8",
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IoSearch style={{ color: "#2db7e8" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Add User button */}
          <MuiButton
            variant="contained"
            color="primary"
            startIcon={<FaPlus />}
            onClick={handleOpenAdd}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              "&:hover": {
                backgroundColor: "#1a8bb3",
              },
            }}
          >
            Add User
          </MuiButton>

          {/* Export button */}

          <ExcelDownloadButton
            data={users.map(
              ({
                user_id,
                user_name,
                user_level,
                mobile_no,
                email,
                is_active,
                remark,
              }) => ({
                "User ID": user_id,
                "User Name": user_name,
                Level: user_level,
                Mobile: mobile_no,
                Email: email,
                Status: is_active ? "Active" : "Inactive",
                Remark: remark,
              })
            )}
            fileName={`${company.name} - User Details`}
          />
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Table>
          <TableHead sx={{ backgroundColor: "#2db7e8" }}>
            <TableRow>
              {[
                "User ID",
                "User Name",
                "Level",
                "Mobile",
                "Email",
                "Status",
                "Remark",
                "Actions",
              ].map((title) => (
                <TableCell
                  key={title}
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                >
                  {title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user, index) => (
              <TableRow
                key={user.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
                  "&:hover": {
                    backgroundColor: "#e3f2fd",
                  },
                }}
              >
                <TableCell align="center">{user?.user_id}</TableCell>
                <TableCell align="center">{user.user_name}</TableCell>
                <TableCell align="center">{user.user_level}</TableCell>
                <TableCell align="center">{user.mobile_no}</TableCell>
                <TableCell align="center">{user.email}</TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color: "#fff",
                      backgroundColor: user.is_active ? "#52b03b" : "#e53935",
                    }}
                  >
                    {user.is_active ? "Active" : "Inactive"}
                  </Box>
                </TableCell>
                <TableCell align="center">{user.remark || "-"}</TableCell>
                <TableCell gap="2" align="center">
                  {/* Edit User Button */}
                  <IconButton
                    onClick={() => {
                      setSelectedUser(user);
                      handleOpenEdit();
                    }}
                    sx={{
                      backgroundColor: "#2db7e8",
                      color: "#fff",
                      mr: 1, // Add some margin between buttons
                      "&:hover": { backgroundColor: "#1a8bb3" },
                    }}
                  >
                    <FaEdit size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Modal */}
      <Modal open={openAdd} onClose={handleCloseAdd}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "400px" },
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: 4,
            p: 3,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Add User</Typography>
            <GrClose style={{ cursor: "pointer" }} onClick={handleCloseAdd} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {Inputs.map((input) =>
              input.name === "user_level" ? (
                <select
                  key={input.name}
                  name={input.name}
                  onChange={handleAddChange}
                  style={{
                    padding: 10,
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    fontSize: 16,
                  }}
                >
                  <option>{input.placeholder}</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="normal">Normal</option>
                </select>
              ) : (
                <input
                  key={input.name}
                  name={input.name}
                  placeholder={input.placeholder}
                  onChange={handleAddChange}
                  style={{
                    padding: 10,
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    fontSize: 16,
                  }}
                />
              )
            )}
            <MuiButton
              variant="contained"
              color="primary"
              disabled={isLoading}
              onClick={() => addUser(add_user)}
            >
              {isLoading ? <ClipLoader size={20} color="#fff" /> : "Add User"}
            </MuiButton>
          </Box>
        </Box>
      </Modal>

      {/* Edit User Modal */}
      <Modal open={openEdit} onClose={handleCloseEdit}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "500px" },
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: 4,
            p: 3,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Edit User</Typography>
            <GrClose style={{ cursor: "pointer" }} onClick={handleCloseEdit} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {EditInputs.map((el) => {
              if (el.name === "is_active") {
                return (
                  <Box
                    key={el.name}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <label>{el.placeholder}</label>
                    <select
                      name={el.name}
                      defaultValue={selected_user[el.name]}
                      onChange={handleEditChange}
                      style={{
                        padding: 10,
                        borderRadius: 6,
                        border: "1px solid #ccc",
                      }}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </Box>
                );
              } else if (el.name === "user_level") {
                return (
                  <Box
                    key={el.name}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <label>{el.placeholder}</label>
                    <select
                      name={el.name}
                      defaultValue={selected_user[el.name]}
                      onChange={handleEditChange}
                      style={{
                        padding: 10,
                        borderRadius: 6,
                        border: "1px solid #ccc",
                      }}
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="normal">Normal</option>
                    </select>
                  </Box>
                );
              } else if (el.name === "password") {
                return (
                  <Box
                    key={el.name}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <label>{el.placeholder}</label>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <input
                        name={el?.name}
                        type={visible_password ? "text" : "password"}
                        defaultValue={selected_user[el.name]}
                        onChange={handleEditChange}
                        style={{
                          padding: 10,
                          borderRadius: 6,
                          border: "1px solid #ccc",
                          flex: 1,
                        }}
                      />
                      <IconButton
                        onClick={() => setVisiblePassword(!visible_password)}
                      >
                        {visible_password ? <FaEyeSlash /> : <FaEye />}
                      </IconButton>
                    </Box>
                  </Box>
                );
              } else {
                return (
                  <Box
                    key={el.name}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <label>{el.placeholder}</label>
                    <input
                      defaultValue={selected_user[el.name]}
                      name={el.name}
                      onChange={handleEditChange}
                      style={{
                        padding: 10,
                        borderRadius: 6,
                        border: "1px solid #ccc",
                      }}
                    />
                  </Box>
                );
              }
            })}
            <MuiButton
              variant="contained"
              color="primary"
              disabled={isLoading}
              onClick={() => editUser(selected_user.id, edit_user)}
            >
              {isLoading ? (
                <ClipLoader size={20} color="#fff" />
              ) : (
                "Update User"
              )}
            </MuiButton>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
